import Hammer from 'hammerjs';

/*
* General vertical slider class can be used for all kinds of items
* that needs to be vertically slidable.
* HTML structure:
* .$el
*   .js-verticalslider-arrow-prev
*   .$contentInner
*   .js-verticalslider-arrow-next
*/
export default class VerticalSlider {
  constructor($el) {
    this.$el = $el;
    this.arrowVisibleClass = 'js-verticalslider-arrow-visible';
    this.$arrowPrev = this.$el.querySelector('.js-verticalslider-arrow-prev');
    this.$arrowNext = this.$el.querySelector('.js-verticalslider-arrow-next');

    this.childNodes = [...$el.children];
    [, this.$contentInner] = this.childNodes;
    this.$items = [...this.$contentInner.children];

    this.itemsLength = this.$items.length;
    this.activeIndex = 0;
    this.itemPositions = [];

    this.lastPosY = this.$contentInner.offsetTop;
    this.isDragging = false;

    this.initBox();
    this.initArrows();
    this.initItemPosition();
    this.initSwipe();

    window.addEventListener('resize', this.handleResize);

    // Handle slide behaviour on tab
    this.$contentInner.addEventListener('focusin', (event) => {
      if (event.target.href) {
        return;
      }

      this.$contentInner.scrollTop = 0;
      const $slide = event.target.closest('.js-verticalslider-item');
      const index = Array.prototype.indexOf.call(this.$items, $slide);
      this.snapToClosestItem(this.itemPositions[index]);
      this.handleArrowVisibility();
    });
  }

  /*
  * Removes fallback styling and adds styling for js use
  */
  initBox = () => {
    this.$el.classList.add('js-verticalslider-initialized');
    this.$arrowNext.classList.add(this.arrowVisibleClass);
    this.$el.querySelectorAll(this.itemClass).forEach(($item) => {
      $item.setAttribute('tabindex', '0');
    });
  }

  /*
  * Adds click events to arrows
  */
  initArrows = () => {
    this.handleArrowVisibility();
    this.$arrowPrev.addEventListener('click', this.handlePrevItem);
    this.$arrowNext.addEventListener('click', this.handleNextItem);
  }

  /*
  * Map item positions to indexes for snappy behaviour
  */
  initItemPosition = () => {
    let spaceAdd = 0;
    this.$items.forEach((item, index) => {
      this.itemPositions[index] = -1 * spaceAdd;
      spaceAdd += item.offsetHeight;
    });
  }

  /*
  * Initiate the draggable content
  */
  initSwipe = () => {
    const mc = new Hammer(this.$contentInner, {
      behavior: {
        userSelect: 'none',
      },
      inputClass: Hammer.TouchInput,
    });

    mc.add(new Hammer.Pan({
      direction: Hammer.DIRECTION_ALL,
      threshold: 0,
    }));

    mc.on('pan', this.handleDrag);
  }

  /*
  * Handle positioning of the innerContent when dragged by swiping (mobile).
  * Lets the content snap to the top of the items.
  */
  handleDrag = (event) => {
    if (!this.isDragging) {
      this.isDragging = true;
    }

    const posY = event.deltaY + this.lastPosY;

    this.$contentInner.style.transform = `translateY(${posY}px)`;

    if (event.isFinal) {
      this.isDragging = false;
      this.snapToClosestItem(posY);
      this.handleArrowVisibility();
    }
  }

  /*
  * Translates the contentInner to the top of the closest item
  */
  snapToClosestItem = (position) => {
    const snapPosition = this.getClosestItemPosition(position);

    this.$contentInner.style.transform = `translateY(${snapPosition}px)`;
    this.activeIndex = this.itemPositions.indexOf(snapPosition);
    this.lastPosY = snapPosition;
  }

  /*
  * Returns the item position with the lowest distance to the current drag position
  */
  getClosestItemPosition = (position) => {
    let curr = this.lastPosY;
    let diff = Math.abs((-1 * position) - curr);

    for (let i = 0; i < this.itemPositions.length; i += 1) {
      const newDiff = Math.abs(position - this.itemPositions[i]);

      if (newDiff < diff) {
        diff = newDiff;
        curr = this.itemPositions[i];
      }
    }

    return curr;
  }

  /*
  * Clickhandler for prev arrow.
  * Moves one item up.
  */
  handlePrevItem = () => {
    if (this.activeIndex > 0) {
      this.activeIndex -= 1;
      this.updateItemPosition();
    }
  }

  /*
  * Clickhandler for next arrow.
  * Moves one item down.
  */
  handleNextItem = () => {
    if (this.activeIndex < this.itemsLength) {
      this.activeIndex += 1;
      this.updateItemPosition();
    }
  }

  /*
  * Update sliding position to the nearest active slide
  */
  updateItemPosition = () => {
    const spaceUntilActiveItem = this.itemPositions[this.activeIndex];
    this.$contentInner.style.transform = `translateY(${spaceUntilActiveItem}px)`;
    this.lastPosY = spaceUntilActiveItem;
    this.activeIndex = this.itemPositions.indexOf(spaceUntilActiveItem);
    this.handleArrowVisibility();
  }

  /*
  * Boolean: returns true if the list of items does not fit into the container's height
  */
  needsScrolling = () => {
    const boxHeight = this.$el.getBoundingClientRect().height;
    let itemsHeight = 1;
    this.$items.forEach(($item) => {
      itemsHeight += $item.getBoundingClientRect().height;
    });
    return itemsHeight > boxHeight;
  }

  /*
  * Hides and shows arrows only when needed.
  */
  handleArrowVisibility = () => {
    this.initItemPosition();
    const needsScrolling = this.needsScrolling();

    if (!needsScrolling) {
      this.visibility(this.$arrowPrev, false);
      this.visibility(this.$arrowNext, false);
    } else {
      const $lastItem = this.$items[this.itemsLength - 1];
      const lastItemPos = this.itemPositions[this.itemPositions.length - 1];
      const maxSnapPosition = (lastItemPos - $lastItem.offsetHeight) +
                              (this.$el.offsetHeight);
      const lastIsInView = this.lastPosY <= maxSnapPosition;

      if (this.activeIndex === 0) {
        this.visibility(this.$arrowPrev, false);
        this.visibility(this.$arrowNext, true);
      } else if (lastIsInView) {
        this.visibility(this.$arrowPrev, true);
        this.visibility(this.$arrowNext, false);
      } else {
        this.visibility(this.$arrowPrev, true);
        this.visibility(this.$arrowNext, true);
      }
    }
  }

  /*
  * Adds a class to an element that handles visibility.
  */
  visibility = ($el, visibility) => {
    if (visibility) {
      $el.classList.add(this.arrowVisibleClass);
    } else {
      $el.classList.remove(this.arrowVisibleClass);
    }
  }

  /*
  * Recalculates the item positions on resize and
  * snaps to the closest item.
  */
  handleResize = () => {
    this.initItemPosition();
    this.snapToClosestItem(this.lastPosY);
  }
}
